import React, { useState } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { COLORS, Row, Col, Separator, Spinner } from '@cuemath/comps';
import { Text, Button } from '@cuemath/web-comps';

import { openModal } from 'actions/modal';
import { MODAL_FORM } from 'constants/form';
import MODAL from 'constants/modal';

const PAYMENT_TYPE_LABEL = { SUBSCRIPTION: 'Subscription', ONE_TIME: 'One-time' };

// Per trial-screen-category guidance shown in place of the plan cards.
const SCREEN_GUIDANCE = {
  SLOT_BOOKING:
    'Parent sees only the slot-booking screen on leap. They can book a slot to start the trial — or, to proceed without a trial (evaluation / known teacher), set a preferred teacher to go straight to the payment screen.',
  PRE_TRIAL:
    "The parent's trial is upcoming — they see the pre-trial screen on leap. To reach the payment/plans screen, the trial must be completed first.",
  ENROLLED: 'This parent is already enrolled.',
};

const SCREEN_HEADER = {
  SLOT_BOOKING: 'Teacher & slots',
  PRE_TRIAL: 'Trial upcoming',
  ENROLLED: 'Enrolled',
};
const GREY = COLORS.GREY_2 || '#666';
const LINE = COLORS.GERANIUM || '#e6e6e6';
const CTA = COLORS.CTA_LINK || '#1a73e8';

const Panel = styled(Col)`
  background: ${COLORS.WHITE};
  border: 1px solid ${LINE};
  border-radius: 8px;
  padding: 16px 20px;
`;

const Title = styled(Text)`
  && {
    font-weight: 700;
  }
`;

const Blurb = styled(Text)`
  color: ${GREY};
`;

const GuideLink = styled.span`
  color: ${CTA};
  font-weight: 600;
  cursor: pointer;
  display: inline-block;
  white-space: nowrap;
  &:hover {
    text-decoration: underline;
  }
`;

// Clean hairline delimiter (replaces the odd grey band a visible Separator drew).
const Divider = styled.div`
  height: 1px;
  background: ${LINE};
  margin: 14px 0;
`;

const ChipsRow = styled(Row)`
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`;

const DefChip = styled(Row)`
  gap: 6px;
  align-items: center;
  background: ${COLORS.BACKGROUND || '#f5f5f5'};
  border-radius: 6px;
  padding: 6px 10px;
`;

const EditButton = styled(Button)`
  && {
    flex: 0 0 auto;
  }
`;

// Loading / error placeholder row, shown in place of the chips + plans while the
// view API is in flight or has failed — keeps the panel box on screen at all times.
const StateRow = styled(Row)`
  gap: 10px;
  align-items: center;
  padding: 6px 0;
`;

const RetryButton = styled(Button)`
  && {
    flex: 0 0 auto;
  }
`;

const FreqLabel = styled(Text)`
  color: ${GREY};
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

// Segmented 2CPW / 3CPW toggle — mirrors how the parent picks frequency on leap.
const FreqTabs = styled(Row)`
  gap: 8px;
  flex-wrap: wrap;
`;

const FreqTab = styled.button`
  border: 1px solid ${p => (p.$on ? CTA : LINE)};
  background: ${p => (p.$on ? 'rgba(26,115,232,0.08)' : COLORS.WHITE)};
  color: ${p => (p.$on ? CTA : COLORS.TEXT || '#222')};
  border-radius: 999px;
  padding: 6px 14px;
  font-weight: 600;
  font-size: 12px;
  cursor: pointer;
`;

const CardsRow = styled(Row)`
  flex-wrap: wrap;
  gap: 12px;
`;

// Actionable amber block — parent is on the leap slot-booking screen and ops
// can set a teacher to move them to the payment screen.
const Notice = styled(Col)`
  background: #fff6e5;
  border: 1px solid #ffe0a3;
  border-radius: 8px;
  padding: 14px 16px;
  gap: 10px;
  align-items: flex-start;
`;

// Neutral informational block for the non-actionable buckets (pre-trial / enrolled).
const InfoBlock = styled(Col)`
  background: ${COLORS.BACKGROUND || '#f5f5f5'};
  border: 1px solid ${LINE};
  border-radius: 8px;
  padding: 14px 16px;
  gap: 10px;
  align-items: flex-start;
`;

const PlanCard = styled(Col)`
  min-width: 168px;
  flex: 1 1 168px;
  max-width: 240px;
  border: 1px solid ${LINE};
  border-radius: 10px;
  padding: 12px 14px;
  gap: 3px;
`;

const Tenure = styled(Text)`
  color: ${GREY};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const PriceLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: 5px;
  white-space: nowrap;
`;

const SavingsBadge = styled(Text)`
  color: ${COLORS.GREEN_2 || '#1a7f37'};
`;

const savingsFor = plan => {
  const pct =
    plan.annual_vs_monthly_discount_percentage ??
    plan.semi_annual_vs_monthly_discount_percentage ??
    plan.quarterly_vs_monthly_discount_percentage;
  const amt =
    plan.annual_savings_amount ??
    plan.semi_annual_savings_amount ??
    plan.quarterly_savings_amount;
  return { pct, amt };
};

function Definition({ label, value }) {
  return (
    <DefChip>
      <Text variant="caption" color={GREY}>
        {label}
      </Text>
      <Text variant="body2Bold">{value}</Text>
    </DefChip>
  );
}

function PlanCards({ plans, symbol }) {
  return (
    <CardsRow>
      {plans.map(plan => {
        const { pct, amt } = savingsFor(plan);
        return (
          <PlanCard key={plan.id}>
            <Tenure variant="caption">{plan.type}</Tenure>
            <PriceLine>
              <Text variant="body1Bold">
                {symbol}
                {plan.price_per_session}
              </Text>
              <Text variant="caption" color={GREY}>
                /session
              </Text>
            </PriceLine>
            <Text variant="body2" color={GREY}>
              {plan.classes} sessions · {symbol}
              {plan.price} total
            </Text>
            {pct ? (
              <SavingsBadge variant="caption">
                Save {pct}%{amt ? ` (${symbol}${amt})` : ''}
              </SavingsBadge>
            ) : null}
          </PlanCard>
        );
      })}
    </CardsRow>
  );
}

function PaymentFlowOverridesPanel({ viewData, onEdit, isLoading, isError, onRetry }) {
  const dispatch = useDispatch();
  const plansEnvelope = viewData?.plans || {};
  const plans = Array.isArray(plansEnvelope.plans) ? plansEnvelope.plans : [];
  const paymentType =
    PAYMENT_TYPE_LABEL[plansEnvelope.payment_mode] || plansEnvelope.payment_mode || '-';
  const defaults = viewData?.effective_defaults || {};
  const symbol = plans[0]?.currency_symbol || viewData?.currency || '';

  // Raw computed admissions state (ignores the skip override) + whether ops has
  // an active skip override on top of it.
  const computedAdmissions = viewData?.computed_admissions;
  const skipAdmissions = viewData?.current_overrides?.skip_admissions_check;

  // Durations config (guard — older backend omits it).
  const durationsCfg = viewData?.durations;
  const defaultDurations = durationsCfg?.default_durations || [];
  const enabledDurations = durationsCfg?.enabled_durations || [];

  // Effective teacher the parent will use (band intentionally omitted by backend).
  const effectiveTeacher = viewData?.effective_teacher;
  const effectiveTeacherId = viewData?.effective_teacher_id;
  const teacherLabel = effectiveTeacher
    ? `${effectiveTeacher.name || 'Teacher'} (${effectiveTeacher.id})`
    : effectiveTeacherId || null;

  // screen_category picks which block renders. Older backend omits it → fall
  // back to payment_screen_state: SLOTS_PENDING → slot-booking guidance,
  // everything else → plans (the current pre-screen_category behavior).
  const legacySlotsPending = viewData?.payment_screen_state === 'SLOTS_PENDING';
  const screenCategory =
    viewData?.screen_category || (legacySlotsPending ? 'SLOT_BOOKING' : 'POST_TRIAL');
  const canEdit = !!viewData?.permissions?.can_set_overrides;

  // Group by classes-per-week. The catalog usually offers a 2/week and a 3/week
  // variant, but either may be missing and a group may hold 0-3 plans — so the
  // frequency toggle is built from whatever frequencies actually came back.
  const byFreq = {};
  plans.forEach(p => {
    const f = p.classes_per_week || 0;
    (byFreq[f] = byFreq[f] || []).push(p);
  });
  const freqs = Object.keys(byFreq)
    .map(Number)
    .sort((a, b) => a - b);

  const [selFreq, setSelFreq] = useState(null);
  // Fall back to the first available frequency if nothing is selected yet or the
  // selection is no longer present (e.g. after an override flips the catalog).
  const activeFreq = selFreq != null && byFreq[selFreq] ? selFreq : freqs[0];

  const openGuide = () =>
    dispatch(
      openModal(
        MODAL_FORM.PAYMENT_FLOW_GUIDE,
        { size: 'custom', width: '860px', height: MODAL.HEIGHT },
        {},
      ),
    );

  return (
    <Panel>
      <Col>
        <Title variant="body1Bold">Payment setup (Admissions)</Title>
        <Separator height={4} transparent />
        <Blurb variant="body2">
          These are the exact plans this parent sees on the leap platform — they pay
          online there (India defaults to one-time; elsewhere subscription), so
          subscription-link generation is off here. For an offline course, generate a
          one-time payment link instead.
        </Blurb>
        <Separator height={6} transparent />
        <GuideLink onClick={openGuide}>Full guide ↗</GuideLink>
      </Col>

      <Divider />

      {isLoading ? (
        <StateRow>
          <Spinner size={16} />
          <Text variant="body2" color={GREY}>
            Loading payment setup…
          </Text>
        </StateRow>
      ) : isError ? (
        <StateRow>
          <Text variant="body2" color={GREY}>
            Couldn&apos;t load the payment setup.
          </Text>
          {onRetry && (
            <RetryButton
              size="xsmall"
              variant="secondary"
              label="Retry"
              onClick={onRetry}
            />
          )}
        </StateRow>
      ) : (
        <>
          <Row justifyContent="space-between" alignItems="center" gap={12}>
            <ChipsRow>
              <Definition label="Payment type" value={paymentType} />
              <Definition
                label="Enrollment type"
                value={plans[0]?.enrollment_type || defaults.plan_tier || '-'}
              />
              <Definition
                label="Gateway"
                value={plans[0]?.payment_client || defaults.payment_gateway || 'default'}
              />
              <Definition label="Grade" value={viewData?.grade ?? '-'} />
              {teacherLabel && <Definition label="Teacher" value={teacherLabel} />}
              {computedAdmissions != null && (
                <Definition
                  label="Enrollment category"
                  value={computedAdmissions ? 'Admission' : 'Renewal'}
                />
              )}
              {skipAdmissions && <Definition label="Skip admissions" value="On" />}
              {durationsCfg && (
                <Definition
                  label="Durations"
                  value={`default ${defaultDurations.join(', ') ||
                    '-'} · enabled ${enabledDurations.join(', ') || '-'}`}
                />
              )}
            </ChipsRow>
            {onEdit && (
              <EditButton
                size="xsmall"
                variant="secondary"
                label="Edit payment setup"
                onClick={onEdit}
              />
            )}
          </Row>

          <Separator height={14} transparent />
          <Text variant="body2Bold">
            {screenCategory === 'POST_TRIAL'
              ? plansEnvelope.session_duration_text || 'Plans'
              : SCREEN_HEADER[screenCategory]}
          </Text>
          <Separator height={10} transparent />

          {screenCategory === 'SLOT_BOOKING' ? (
            <Notice>
              <Text variant="body2" color={GREY}>
                {SCREEN_GUIDANCE.SLOT_BOOKING}
              </Text>
              {onEdit && canEdit && (
                <Button
                  size="xsmall"
                  variant="secondary"
                  label="Set teacher"
                  onClick={onEdit}
                />
              )}
            </Notice>
          ) : screenCategory === 'PRE_TRIAL' || screenCategory === 'ENROLLED' ? (
            <InfoBlock>
              <Text variant="body2" color={GREY}>
                {SCREEN_GUIDANCE[screenCategory]}
              </Text>
            </InfoBlock>
          ) : plans.length ? (
            <>
              {freqs.length > 1 ? (
                <FreqTabs>
                  {freqs.map(freq => (
                    <FreqTab
                      key={freq}
                      type="button"
                      $on={activeFreq === freq}
                      onClick={() => setSelFreq(freq)}
                    >
                      {freq} sessions / week
                    </FreqTab>
                  ))}
                </FreqTabs>
              ) : (
                <FreqLabel variant="caption">{activeFreq} sessions / week</FreqLabel>
              )}
              <Separator height={10} transparent />
              <PlanCards plans={byFreq[activeFreq] || []} symbol={symbol} />
            </>
          ) : (
            <Text variant="body2" color={GREY}>
              No plans returned for this student.
            </Text>
          )}
        </>
      )}
    </Panel>
  );
}

export default PaymentFlowOverridesPanel;
